import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const widthKeyForCol = (col, index) =>
  col.dataIndex != null
    ? col.dataIndex
    : col.key != null
      ? String(col.key)
      : `__col_${index}`;

const loadPersistedWidths = (persistenceKey) => {
  if (!persistenceKey || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(persistenceKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const persistWidths = (persistenceKey, widths) => {
  if (!persistenceKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(persistenceKey, JSON.stringify(widths));
  } catch {
    /* ignore quota / private mode */
  }
};

/** Apply width to the header cell and matching body cells in the same table section. */
const applyWidthToColumnSection = (th, widthPx) => {
  if (!th) return;
  const w = `${widthPx}px`;
  th.style.width = w;
  th.style.minWidth = w;

  const headerRow = th.parentElement;
  if (!headerRow) return;
  const colIndex = Array.from(headerRow.children).indexOf(th);
  if (colIndex < 0) return;

  const section =
    th.closest(".ant-table-fixed-left") ||
    th.closest(".ant-table-fixed-right") ||
    th.closest(".ant-table-content") ||
    th.closest(".ant-table-container");
  if (!section) return;

  section.querySelectorAll(".ant-table-thead tr, .ant-table-tbody tr").forEach((row) => {
    const cell = row.children[colIndex];
    if (cell) {
      cell.style.width = w;
      cell.style.minWidth = w;
    }
  });
};

/** Drag from the right edge of the header cell to resize (does not alter title DOM). */
const RESIZE_ZONE_PX = 12;
const GUIDE_CLASS = "table-col-resize-guide";

const withResizableColumns = (WrappedTableComponent) => {
  function ResizableColumnsComponent(props) {
    const {
      columns: incomingColumns = [],
      columnWidthPersistenceKey,
      onColumnWidthsChange,
      ...rest
    } = props;
    const [columnWidths, setColumnWidthsState] = useState(() =>
      loadPersistedWidths(columnWidthPersistenceKey)
    );
    const dragRef = useRef(null);

    useEffect(() => {
      setColumnWidthsState(loadPersistedWidths(columnWidthPersistenceKey));
    }, [columnWidthPersistenceKey]);

    const setColumnWidths = useCallback(
      (updater) => {
        setColumnWidthsState((prev) => {
          const next = typeof updater === "function" ? updater(prev) : updater;
          if (columnWidthPersistenceKey) {
            persistWidths(columnWidthPersistenceKey, next);
          }
          if (typeof onColumnWidthsChange === "function") {
            onColumnWidthsChange(next);
          }
          return next;
        });
      },
      [columnWidthPersistenceKey, onColumnWidthsChange]
    );

    const columnsWithResizer = useMemo(
      () =>
        incomingColumns.map((col, index) => {
          if (col.hideInTable) {
            return { ...col };
          }
          const wKey = widthKeyForCol(col, index);
          const mergedWidth = columnWidths[wKey] ?? col.width;

          const onHeaderCell = (headerCol) => {
            const prev =
              typeof col.onHeaderCell === "function"
                ? col.onHeaderCell(headerCol)
                : col.onHeaderCell || {};
            const prevClass = prev.className;
            const prevOnMouseDown = prev.onMouseDown;

            return {
              ...prev,
              className: [prevClass, "table-col-resize-edge"].filter(Boolean).join(" "),
              onMouseDown: (e) => {
                if (e.button !== 0) {
                  prevOnMouseDown?.(e);
                  return;
                }
                const th = e.currentTarget;
                const rect = th.getBoundingClientRect();
                const fromRight = rect.right - e.clientX;
                if (fromRight > RESIZE_ZONE_PX) {
                  prevOnMouseDown?.(e);
                  return;
                }

                e.preventDefault();
                e.stopPropagation();

                const startWidth = rect.width;
                dragRef.current = {
                  widthKey: wKey,
                  startX: e.clientX,
                  startWidth,
                  lastWidth: startWidth,
                  th,
                };

                th.classList.add("table-col-resize-active-th");
                document.body.classList.add("table-col-resize-dragging");

                const guide = document.createElement("div");
                guide.className = GUIDE_CLASS;
                guide.style.left = `${e.clientX}px`;
                document.body.appendChild(guide);

                document.body.style.userSelect = "none";

                const onMove = (ev) => {
                  const d = dragRef.current;
                  if (!d) return;
                  ev.preventDefault();
                  guide.style.left = `${ev.clientX}px`;
                  const next = Math.max(48, d.startWidth + (ev.clientX - d.startX));
                  d.lastWidth = next;
                  applyWidthToColumnSection(d.th, next);
                };

                let blurHandler;
                const onUp = () => {
                  document.removeEventListener("mousemove", onMove, true);
                  document.removeEventListener("mouseup", onUp, true);
                  if (blurHandler) {
                    window.removeEventListener("blur", blurHandler);
                    blurHandler = null;
                  }

                  if (guide.parentNode) {
                    guide.remove();
                  }
                  document.body.classList.remove("table-col-resize-dragging");
                  document.body.style.userSelect = "";

                  const d = dragRef.current;
                  dragRef.current = null;

                  if (!d) return;

                  const finalW = d.lastWidth ?? d.startWidth;

                  if (d.th) {
                    d.th.classList.remove("table-col-resize-active-th");
                    d.th.style.width = "";
                    d.th.style.minWidth = "";
                    d.th.classList.add("table-col-resize-released");
                    const thEl = d.th;
                    window.setTimeout(() => {
                      thEl.classList.remove("table-col-resize-released");
                    }, 220);
                  }

                  setColumnWidths((prev) => {
                    if (prev[d.widthKey] === finalW) return prev;
                    return { ...prev, [d.widthKey]: finalW };
                  });
                };

                blurHandler = () => onUp();
                window.addEventListener("blur", blurHandler);
                document.addEventListener("mousemove", onMove, true);
                document.addEventListener("mouseup", onUp, true);
              },
            };
          };

          return {
            ...col,
            width: mergedWidth,
            onHeaderCell,
          };
        }),
      [incomingColumns, columnWidths, setColumnWidths]
    );

    return <WrappedTableComponent {...rest} columns={columnsWithResizer} />;
  }

  ResizableColumnsComponent.displayName = `ResizableColumns(${
    WrappedTableComponent.displayName || WrappedTableComponent.name || "Table"
  })`;

  return ResizableColumnsComponent;
};

export default withResizableColumns;
